import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import "./NewTree.css";
import {analyzePrompt} from "../../api/Client";

type TreeLength = "SHORT" | "MEDIUM" | "LONG";
type TreeMode = "learn" | "study";

const LENGTH_OPTIONS: { value: TreeLength; label: string }[] = [
    { value: "SHORT", label: "Short" },
    { value: "MEDIUM", label: "Medium" },
    { value: "LONG", label: "Long" },
];

const MODE_OPTIONS: { value: TreeMode; label: string }[] = [
    { value: "learn", label: "Learn" },
    { value: "study", label: "Study" },
];

function SegmentedControl<T extends string>({
    options,
    value,
    onChange,
    ariaLabel,
}: {
    options: { value: T; label: string }[];
    value: T;
    onChange: (value: T) => void;
    ariaLabel: string;
}) {
    return (
        <div className="segmented-control" role="radiogroup" aria-label={ariaLabel}>
            {options.map((option) => (
                <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={value === option.value}
                    className={
                        "segmented-control__option" +
                        (value === option.value ? " segmented-control__option--active" : "")
                    }
                    onClick={() => onChange(option.value)}
                >
                    {option.label}
                </button>
            ))}
        </div>
    );
}

export default function NewTree() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [description, setDescription] = useState("");
    const [file, setFile] = useState<File | null>(null);
    const [length, setLength] = useState<TreeLength>("MEDIUM");
    const [mode, setMode] = useState<TreeMode>("learn");

    const fileInputRef = useRef<HTMLInputElement>(null);

    const generateMutation = useMutation({
        mutationFn: () => analyzePrompt(description, file, length),
        onSuccess: async (res) => {
            // The sidebar's tree list has no reason to know a job was just
            // created — force it to refetch so the new (generating) tree appears.
            await queryClient.invalidateQueries({ queryKey: ["jobs"] });
            navigate(`/learn/${res.id}`);
        },
    });

    const handleGenerate = () => {
        generateMutation.mutate();
    };

    return (
        <div className="new-tree-panel">
            <div className="new-tree-content">
                <section className="new-tree-section new-tree-section--textarea">
                    <textarea
                        aria-label="Describe your course"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="What would you like to learn?"
                    />
                </section>

                <section className="new-tree-section">
                    <button
                        className="new-tree-upload"
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {file ? file.name : "Upload PDFs, slides, notes, documents..."}
                    </button>

                    <input
                        ref={fileInputRef}
                        hidden
                        type="file"
                        accept=".pdf,.ppt,.pptx,.doc,.docx,.txt,.md"
                        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                    />
                </section>

                {/*<section className="new-tree-section">*/}
                {/*    <SegmentedControl*/}
                {/*        options={MODE_OPTIONS}*/}
                {/*        value={mode}*/}
                {/*        onChange={setMode}*/}
                {/*        ariaLabel="Learn or study mode"*/}
                {/*    />*/}
                {/*</section>*/}

                <section className="new-tree-section">
                    <SegmentedControl
                        options={LENGTH_OPTIONS}
                        value={length}
                        onChange={setLength}
                        ariaLabel="Tree length"
                    />
                </section>
            </div>

            <footer className="new-tree-footer">
                {generateMutation.isError && (
                    <p className="new-tree-error" role="alert">
                        Couldn't generate a tree from that. Try again.
                    </p>
                )}

                <button
                    onClick={handleGenerate}
                    className="button button--primary"
                    disabled={generateMutation.isPending || description.length === 0}
                >
                    {generateMutation.isPending ? "Generating..." : "Generate Tree"}
                </button>
            </footer>
        </div>
    );
}
